import { supabase } from '../supabaseClient';

/**
 * Get the date range for a specific week offset
 * @param {number} weekOffset - The week offset (0 = current week, +1 = next week, -1 = last week)
 * @returns {Object} Object with startDate and endDate in YYYY-MM-DD format
 */
const getWeekDateRange = (weekOffset) => {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1 + weekOffset * 7);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 4); // Friday (Monday + 4 days)

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(startOfWeek),
    endDate: formatDate(endOfWeek),
  };
};

/**
 * Fetch all attendance bookings for a specific week
 * @param {number} weekOffset - The week offset (0 = current week, +1 = next week, -1 = last week)
 * @returns {Promise<Array>} Array of attendance bookings
 * @throws {Error} If database query fails
 */
export const fetchAttendanceForWeek = async (weekOffset) => {
  const { startDate, endDate } = getWeekDateRange(weekOffset);

  const { data, error } = await supabase
    .from('AttendanceBooking')
    .select('*')
    .gte('date', startDate)
    .lte('date', endDate);

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

/**
 * Create or update an attendance booking for a user on a specific date
 * @param {string} userId - The user UUID
 * @param {string} date - The date in YYYY-MM-DD format
 * @param {string} status - The attendance status ('office' or 'remote')
 * @returns {Promise<Object>} The created/updated attendance booking
 * @throws {Error} If parameters are missing or database operation fails
 */
export const upsertAttendanceBooking = async (userId, date, status) => {
  if (!userId || !date || !status) {
    throw new Error('User ID, date, and status are required');
  }

  // Check if booking exists
  const { data: existing, error: fetchError } = await supabase
    .from('AttendanceBooking')
    .select('*')
    .eq('user', userId)
    .eq('date', date)
    .maybeSingle();

  if (fetchError) {
    throw new Error(fetchError.message);
  }

  if (existing) {
    // Update existing booking
    const { data, error } = await supabase
      .from('AttendanceBooking')
      .update({ status })
      .eq('id', existing.id)
      .select();

    if (error) {
      throw new Error(error.message);
    }

    return data[0];
  } else {
    // Insert new booking
    const { data, error } = await supabase
      .from('AttendanceBooking')
      .insert({
        user: userId,
        date: date,
        status: status,
      })
      .select();

    if (error) {
      throw new Error(error.message);
    }

    return data[0];
  }
};

/**
 * Bulk create or update attendance bookings
 * @param {Array} bookings - Array of booking objects with {user, date, status}
 * @returns {Promise<Array>} Array of created/updated attendance bookings
 * @throws {Error} If bookings is not an array or database operation fails
 */
export const bulkUpsertAttendanceBookings = async (bookings) => {
  if (!Array.isArray(bookings) || bookings.length === 0) {
    if (!Array.isArray(bookings)) {
      throw new Error('Bookings must be a non-empty array');
    }
    return [];
  }

  // Fetch existing bookings for the given user-date pairs
  const existingPromises = bookings.map(booking =>
    supabase
      .from('AttendanceBooking')
      .select('*')
      .eq('user', booking.user)
      .eq('date', booking.date)
      .maybeSingle()
  );

  const existingResults = await Promise.all(existingPromises);

  // Separate into updates and inserts
  const toUpdate = [];
  const toInsert = [];

  bookings.forEach((booking, index) => {
    const { data: existing, error } = existingResults[index];

    if (error) {
      throw new Error(error.message);
    }

    if (existing) {
      toUpdate.push({
        id: existing.id,
        status: booking.status,
      });
    } else {
      toInsert.push(booking);
    }
  });

  const results = [];

  // Perform updates
  if (toUpdate.length > 0) {
    for (const update of toUpdate) {
      const { data, error } = await supabase
        .from('AttendanceBooking')
        .update({ status: update.status })
        .eq('id', update.id)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      if (data && data.length > 0) {
        results.push(data[0]);
      }
    }
  }

  // Perform inserts
  if (toInsert.length > 0) {
    const { data, error } = await supabase
      .from('AttendanceBooking')
      .insert(toInsert)
      .select();

    if (error) {
      throw new Error(error.message);
    }

    if (data) {
      results.push(...data);
    }
  }

  return results;
};
